/* eslint-disable no-nested-ternary */
import React, {
  Component, Fragment, useState, useEffect,
} from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Upload, Select, Modal, DatePicker, Switch, Checkbox, Alert, Table, Popconfirm,
} from 'antd';
import {
  CloseOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import H3FormInput from '@Uilib/h3FormInput';
import ProductActions from '@Actions/productActions';
import ExpenseActions from '@Actions/purchase/expense/expenseActions';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import H3Image from '@Uilib/h3Image';
import H3Text from '@Uilib/h3Text';
import Constants, { S3_URL } from '@Apis/constants';
import imageDefault from '@Images/icons/imageDefault.png';
import './style.scss';
import UserActions from '@Actions/userActions';
import TenantActions from '@Actions/tenantActions';
import RichTextEditor from '@Components/Common/RichTextEditor';
import Helpers from '@Apis/helpers';
import SelectDepartment from '@Components/Common/SelectDepartment';
import Crown from '@Images/crown2.png';
import SelectExpenseAccount from '../../../Common/SelectExpenseAccount';
import SelectSeller from '../../../Common/SelectSeller';
import SelectCustomer from '../../../Common/SelectCustomer';
import SelectTAX from '../../../Admin/Common/SelectTax';
import PRZSelect from '../../../Common/UI/PRZSelect';
import DocumentNumberSeqInput from '../../../Admin/Common/DocumentNumberSeqInput';

const { Option } = Select;

/**
 *
 */
const ExpenseForm = ({
  user, createExpense, updateExpense, createExpenseLoading, updateExpenseLoading, selectedExpense, getRoles, callback, taxes,
}) => {
  const [title, setTitle] = useState('');
  const [fileList, setFileList] = useState([]);
  const [remark, setRemark] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [expenseDate, setExpenseDate] = useState(dayjs());
  const [expenseAccount, setExpenseAccount] = useState('');
  const [expenseType, setExpenseType] = useState('');
  const [department, setDepartment] = useState('');
  const [amount, setAmount] = useState('');
  const [taxId, setTaxId] = useState('');
  const [taxInfo, setTaxInfo] = useState('');
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [expNumber, setExpNumber] = useState('');
  const [docSeqId, setDocSeqId] = useState(null);
  const [initialExpNumber, setInitialExpNumber] = useState('');

  useEffect(() => {
    if (taxes?.data?.[0]?.tax_id) {
      setTaxId(taxes?.data?.[0]?.tax_id);
      setTaxInfo({
        tax_id: taxes?.data?.[0]?.tax_id, tax_name: taxes?.data?.[0]?.tax_name, tax_value: taxes?.data?.[0]?.tax_value, org_id: user?.tenant_info?.org_id, is_active: true,
      });
    }
  }, [taxes]);

  useEffect(() => {
    if (selectedExpense) {
      setTitle(selectedExpense?.expense_title);
      setFileList(selectedExpense?.attachments);
      setRemark(selectedExpense?.remarks);
      setExpenseDate(dayjs(selectedExpense?.expense_date));
      setExpenseAccount(selectedExpense?.expense_account_id);
      setExpenseType(selectedExpense?.expense_type);
      setDepartment(selectedExpense?.department_id);
      setAmount(selectedExpense?.amount_without_tax);
      setTaxId(selectedExpense?.tax_id);
      setTaxInfo(selectedExpense?.tax_info);
      setSelectedSeller(selectedExpense?.seller_id);
      setSelectedCustomer(selectedExpense?.customer_id);
      setExpNumber(selectedExpense?.exp_number || '');
    }
  }, []);

  // const expenseAccounts = user?.tenant_info?.expense_accounts;

  const expenseTypes = [
    {
      label: 'Cost of Goods Sold',
      value: 'Cost of Goods Sold',
    },
    {
      label: 'Labor',
      value: 'Labor',
    },
    {
      label: 'Employee Benefits',
      value: 'Employee Benefits',
    },
    {
      label: 'Utility Bills',
      value: 'Utility Bills',
    },
    {
      label: 'Office Expenses',
      value: 'Office Expenses',
    },
    {
      label: 'Advertising & Marketing',
      value: 'Advertising & Marketing',
    },
    {
      label: 'Fuel/Mileage Expense',
      value: 'Fuel/Mileage Expense',
    },
    {
      label: 'Repairs & Maintenance',
      value: 'Repairs & Maintenance',
    },
    {
      label: 'Meals & Entertainment',
      value: 'Meals & Entertainment',
    },
    {
      label: 'Logistics',
      value: 'Logistics',
    },
    {
      label: 'Travel Expense',
      value: 'Travel Expense',
    },
    {
      label: 'Telephone Expense',
      value: 'Telephone Expense',
    },
    {
      label: 'Miscellaneous',
      value: 'Miscellaneous',
    },
  ];

  const getBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      // eslint-disable-next-line no-param-reassign
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  const isDataValid = () => {
    let isValid = true;
    if (!title || !taxId || !department || !expenseAccount || !amount || !expenseType || !expNumber) {
      isValid = false;
    }
    return isValid;
  };

  const addExpense = () => {
    setFormSubmitted(true);
    if (isDataValid()) {
      if (selectedExpense) {
        const payload = {
          expense_id: selectedExpense?.expense_id,
          expense_account_id: expenseAccount,
          expense_date: expenseDate,
          expense_type: expenseType,
          expense_title: title,
          remarks: remark,
          tenant_id: user?.tenant_info?.tenant_id,
          department_id: department,
          attachments: fileList?.map((item) => ({ url: item?.url ? item?.url : item.response.response.location })) || [],
          customer_id: selectedCustomer,
          seller_id: selectedSeller,
          tax_id: taxId,
          tax_group_info: taxInfo,
          amount_without_tax: amount,
          exp_number: expNumber || '',
        };
        updateExpense(payload, () => {
          callback();
        });
      } else {
        const payload = {
          expense_account_id: expenseAccount,
          expense_date: expenseDate,
          expense_type: expenseType,
          expense_title: title,
          remarks: remark,
          tenant_id: user?.tenant_info?.tenant_id,
          department_id: department,
          attachments: fileList?.map((item) => ({ url: item?.url ? item?.url : item.response.response.location })) || [],
          customer_id: selectedCustomer,
          seller_id: selectedSeller,
          tax_id: taxId,
          tax_group_info: taxInfo,
          amount_without_tax: amount,
          exp_number: initialExpNumber?.toLowerCase()?.trim() === expNumber?.toLowerCase()?.trim() ? null : expNumber,
          seq_id: docSeqId || null,
        };
        createExpense(payload, () => {
          callback();
        });
      }
    }
  };

  const expense = user?.tenant_info?.purchase_config?.addons?.expense?.is_active;

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );
  return (
    <div className="new-product-form__container">
      {false ? (
        <div>
          <div className="ant-row">
            {[1, 2, 3, 4, 5, 6]?.map((item, index) => (
              <div key={item} className="ant-col-md-12 ant-col-xs-24">
                <div className={(index % 2 === 0 ? 'orgInputContainer product-form__right-input' : 'orgInputContainer product-form__left-input')}>
                  <div className="loadingBlock" style={{ height: '15px', marginBottom: '6px' }} />
                  <div className="loadingBlock" style={{ height: '32px' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Fragment>
          <div style={{ marginBottom: '41px' }}>
            <div className="ant-row">
              <div className="ant-col-md-24 ant-col-xs-24 add-product__section">
                <div className="ant-row">
                  <DocumentNumberSeqInput
                    valueFromProps={expNumber}
                    updateCase={!!selectedExpense}
                    setInitialDocSeqNumber={(value) => setInitialExpNumber(value)}
                    entityName="EXPENSE"
                    docSeqId={docSeqId}
                    tenantId={user?.tenant_info?.tenant_id}
                    onChangeFromProps={(event, newValue, seqId) => {
                      setExpNumber(newValue ? (newValue || '') : (event?.target?.value || ''));
                      setDocSeqId(seqId);
                    }}
                    docTitle="Expense#"
                    formSubmitted={formSubmitted}
                    rowClassName="ant-col-md-12 ant-col-xs-24"
                    inputRowClassName="create__right-input"
                    labelClassName="orgFormLabel"
                    inputClassName="orgFormInput"
                    containerClassName=""
                    seqClassName="sqn__selector"
                    selectorStyle={{
                      opacity: 0,
                      backgroundColor: 'transparent',
                      zIndex: 1,
                      top: '-10px',
                      width: '35%',
                    }}
                  />
                  <div className="ant-col-md-12 ant-col-xs-24">
                    <H3FormInput
                      name="expense title"
                      type="text"
                      containerClassName="orgInputContainer product-form__left-input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput"
                      placeholder=""
                      onChange={(event) => setTitle(event.target.value)}
                      value={title}
                      disabled={createExpenseLoading || updateExpenseLoading}
                      label="Expense Title"
                      maxlength="100"
                      required
                      showError={formSubmitted && !title}
                    />
                  </div>
                  <div className="ant-col-md-12 ant-col-xs-24">
                    <div className="orgInputContainer create__right-input">
                      <label className="orgFormLabel">
                        Expense Date
                      </label>
                      <DatePicker
                        value={dayjs(new Date(expenseDate))}
                        onChange={(value) => {
                          setExpenseDate(value);
                        }}
                        disabled={createExpenseLoading || updateExpenseLoading}
                        style={{
                          border: '1px solid rgba(68, 130, 218, 0.2)',
                          borderRadius: '2px',
                          height: '28px',
                          width: '100%',
                          fontSize: '13px',
                        }}
                      />
                    </div>
                  </div>
                  {/* <div className="ant-col-md-12 ant-col-xs-24">
                    <div className="orgInputContainer product-form__right-input">
                      <label className="orgFormLabel">
                        Expense Account
                        <span style={{ color: 'red' }}>{'  *'}</span>
                      </label>
                      <Select
                        value={expenseAccount}
                        disabled={createExpenseLoading || updateExpenseLoading}
                        onChange={(value) => {
                          setExpenseAccount(value);
                        }}
                        bordered={false}
                        options={expenseAccounts?.map((item) => ({
                          label: item?.account_name,
                          value: item?.account_id,
                        }))}
                        style={{
                          border: '1px solid rgba(68, 130, 218, 0.2)',
                          borderRadius: '2px',
                          height: '32px',
                          width: '100%',
                          fontSize: '13px',
                        }}
                      />
                    </div>
                  </div> */}
                  <div className="ant-col-md-12 ant-col-xs-24">
                    <SelectExpenseAccount
                      expenseAccount={expenseAccount}
                      onChange={(value) => {
                        setExpenseAccount(value?.account_id);
                      }}
                      containerClass="orgInputContainer product-form__left-input"
                      inputClassName=" create-po-seller__selector create-po__input-row__input"
                      labelClassName="orgFormLabel"
                      disabled={createExpenseLoading || updateExpenseLoading}
                      showAddExpenseAccount
                    />
                  </div>
                  <div className="ant-col-md-12 ant-col-xs-24">
                    <div className="orgInputContainer product-form__right-input">
                      <label className="orgFormLabel">
                        Expense Types
                        <span style={{ color: 'red' }}>{'  *'}</span>
                      </label>
                      <PRZSelect
                        value={expenseType}
                        disabled={createExpenseLoading || updateExpenseLoading}
                        onChange={(value) => {
                          setExpenseType(value);
                        }}
                        showSearch
                        options={expenseTypes}
                      />
                    </div>
                  </div>

                  <div className="ant-col-md-12 ant-col-xs-24">
                    <H3FormInput
                      name="amount"
                      type="text"
                      containerClassName="orgInputContainer product-form__left-input"
                      labelClassName="orgFormLabel"
                      inputClassName="orgFormInput"
                      placeholder=""
                      onChange={(event) => setAmount(event.target.value)}
                      value={amount}
                      disabled={createExpenseLoading || updateExpenseLoading}
                      label="Enter Amount"
                      required
                      showError={formSubmitted && !title}
                    />
                  </div>

                  <div className="ant-col-md-12 ant-col-xs-24">
                    <SelectTAX
                      title="Select Tax (if applicable)"
                      onChange={(selectedTAX) => {
                        setTaxId(selectedTAX?.tax_id);
                        setTaxInfo(selectedTAX);
                      }}
                      containerClassName="orgInputContainer product-form__right-input"
                      showError={formSubmitted && !taxId}
                      selectedGST={taxId}
                      disabled={createExpenseLoading || updateExpenseLoading}
                      notRequired
                    />
                  </div>

                  <div className="ant-col-md-12 ant-col-xs-24">
                    <SelectSeller
                      selectedSeller={selectedSeller}
                      onChange={(value) => {
                        setSelectedSeller(value?.seller_id);
                      }}
                      containerClass="orgInputContainer product-form__left-input"
                      inputClassName=" create-po-seller__selector create-po__input-row__input"
                      labelClassName="orgFormLabel"
                      disabled={createExpenseLoading || updateExpenseLoading}
                      notRequired
                      mapSellerId
                    />
                  </div>

                  <div className="ant-col-md-12 ant-col-xs-24">
                    <SelectCustomer
                      title="Select Customer"
                      selectedCustomer={selectedCustomer}
                      onChange={(value) => setSelectedCustomer(value?.customer_id)}
                      disabled={createExpenseLoading || updateExpenseLoading}
                      labelClassName="create-sales-order__input-row__label orgFormLabel"
                      inputClassName=""
                      containerClass="product-form__right-input orgInputContainer"
                    />
                  </div>

                  <div className="ant-col-md-12 ant-col-xs-24">
                    <SelectDepartment
                      title="Select Department"
                      selectedDepartment={department}
                      showSearch
                      onChange={(value) => {
                        setDepartment(value?.department_id);
                        getRoles(user?.tenant_info?.tenant_id, value?.tenant_department_id);
                      }}
                      tenantId={user?.tenant_info?.tenant_id}
                      containerClassName="product-form__left-input"
                      labelClassName="orgFormLabel"
                      emptyNotAllowed
                      noDropdownAlign
                      disabled={createExpenseLoading || updateExpenseLoading || selectedExpense}
                    />
                    {formSubmitted && !department && (
                      <div className="input-error">
                        *Please select department
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="ant-col-md-24 ant-col-xs-24">
                <div style={{ marginTop: '15px' }}>
                  <div className="ant-row">
                    <div className="ant-col-md-24 ant-col-xs-24" style={{ marginBottom: '20px' }}>
                      <H3FormInput
                        type="text"
                        containerClassName="orgInputContainer"
                        labelClassName="orgFormLabel"
                        inputClassName="orgFormInput"
                        placeholder=""
                        onChange={(event) => setRemark(event.target.value)}
                        value={remark}
                        label="Additional Remarks"
                        disabled={createExpenseLoading || updateExpenseLoading}
                      />
                    </div>
                    <div className="ant-col-md-24 ant-col-xs-24">
                      <div className="product-form__upload">
                        <label className="orgFormLabel">
                          Attachment(s)
                          {' '}
                        </label>
                        <Upload
                          action={Constants.UPLOAD_FILE}
                          listType="picture-card"
                          fileList={fileList}
                          onPreview={handlePreview}
                          accept="image/*"
                          onChange={(fileListData) => setFileList(fileListData.fileList)}
                          disabled={createExpenseLoading || updateExpenseLoading}
                          multiple
                        >
                          {fileList?.length >= 20 ? null : uploadButton}
                        </Upload>
                        <Modal
                          open={previewVisible}
                          title={previewTitle}
                          footer={null}
                          onCancel={() => setPreviewVisible(false)}
                        >
                          <img alt="example" style={{ width: '100%' }} src={previewImage} />
                        </Modal>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="custom-drawer__footer">
            <div className="ant-col-md-6" style={{ marginTop: '0.5rem' }}>
              <div className="indent-barcode__wrapper" style={{ display: 'flex' }}>
                <H3Button
                  buttonType={defaultButtonTypes.BLUE_ROUNDED}
                  text={selectedExpense ? 'Update Expense' : 'Add Expense'}
                  onClick={() => addExpense()}
                  isLoading={createExpenseLoading || updateExpenseLoading}
                  disabled={createExpenseLoading || updateExpenseLoading || !expense}
                  style={{
                    width: '200px',
                    borderRadius: '5px',
                    padding: '7px 0px',
                    marginLeft: '0',
                  }}
                />
                {!expense && (
                  <Popconfirm
                    placement="topRight"
                    title="This feature is not accessible within your current plan to use this feature contact us."
                    onConfirm={() => window.Intercom('showNewMessage')}
                    okText="Contact Us"
                    cancelText="Cancel"
                  >
                    <img
                      className="barcode-restrict"
                      src={Crown}
                      alt="premium"
                      style={{
                        marginLeft: '8px',
                        marginTop: '5px',
                      }}
                    />
                  </Popconfirm>
                )}
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </div>
  );
};

const mapStateToProps = ({
  UserReducers, ExpenseReducers, TaxReducers,
}) => ({
  user: UserReducers.user,
  createExpenseLoading: ExpenseReducers.createExpenseLoading,
  roles: UserReducers.roles,
  updateExpenseLoading: ExpenseReducers.updateExpenseLoading,
  taxes: TaxReducers.taxes,
});

const mapDispatchToProps = (dispatch) => ({
  createExpense: (payload, callback) => dispatch(ExpenseActions.createExpense(payload, callback)),
  updateExpense: (payload, callback) => dispatch(ExpenseActions.updateExpense(payload, callback)),
  getRoles: (tenantId, tenantDepartmentId) => dispatch(UserActions.getRoles(tenantId, tenantDepartmentId)),
});

ExpenseForm.propTypes = {
  user: PropTypes.any,
  taxes: PropTypes.any,
  createExpense: PropTypes.func,
  updateExpense: PropTypes.func,
  createExpenseLoading: PropTypes.bool,
  updateExpenseLoading: PropTypes.bool,
  getRoles: PropTypes.func,
  selectedExpense: PropTypes.any,
  callback: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ExpenseForm));
